import { useState } from "react";
import { toast } from "react-hot-toast";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { sendOtp } from "../../../services/operations/authAPI";
import { setSignupData } from "../../../slices/authSlice";
import { ACCOUNT_TYPE } from "../../../utils/constants";
import { DS } from "../../../utils/designSystem";
import Tab from "../../common/Tab";

function SignupForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [accountType, setAccountType] = useState(ACCOUNT_TYPE.STUDENT);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { firstName, lastName, email, password, confirmPassword } = formData;

  const handleOnChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    dispatch(setSignupData({ ...formData, accountType }));
    dispatch(sendOtp(formData.email, navigate));
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
    setAccountType(ACCOUNT_TYPE.STUDENT);
  };

  const tabData = [
    { id: 1, tabName: "Student",    type: ACCOUNT_TYPE.STUDENT },
    { id: 2, tabName: "Instructor", type: ACCOUNT_TYPE.INSTRUCTOR },
  ];

  return (
    <form onSubmit={handleOnSubmit} className="flex flex-col gap-5">
      <div className="mb-1">
        <h2 className="text-2xl font-bold text-richblack-5">Create your account</h2>
        <p className="mt-1 text-sm text-richblack-400">
          Start your learning journey today — it's free.
        </p>
      </div>

      <Tab tabData={tabData} field={accountType} setField={setAccountType} />

      {/* Name row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="sf-firstName" className={DS.input.label}>
            First Name <sup className="text-pink-200">*</sup>
          </label>
          <input
            id="sf-firstName"
            required
            type="text"
            name="firstName"
            value={firstName}
            onChange={handleOnChange}
            placeholder="First name"
            className={DS.input.base}
          />
        </div>
        <div>
          <label htmlFor="sf-lastName" className={DS.input.label}>
            Last Name <sup className="text-pink-200">*</sup>
          </label>
          <input
            id="sf-lastName"
            required
            type="text"
            name="lastName"
            value={lastName}
            onChange={handleOnChange}
            placeholder="Last name"
            className={DS.input.base}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="sf-email" className={DS.input.label}>
          Email Address <sup className="text-pink-200">*</sup>
        </label>
        <input
          id="sf-email"
          required
          type="email"
          name="email"
          value={email}
          onChange={handleOnChange}
          placeholder="you@example.com"
          className={DS.input.base}
        />
      </div>

      {/* Password row */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="sf-password" className={DS.input.label}>
            Password <sup className="text-pink-200">*</sup>
          </label>
          <div className="relative">
            <input
              id="sf-password"
              required
              type={showPassword ? "text" : "password"}
              name="password"
              value={password}
              onChange={handleOnChange}
              placeholder="Create password"
              className={`${DS.input.base} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-richblack-400 transition-colors hover:text-richblack-200"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <AiOutlineEyeInvisible size={18} />
              ) : (
                <AiOutlineEye size={18} />
              )}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="sf-confirmPassword" className={DS.input.label}>
            Confirm <sup className="text-pink-200">*</sup>
          </label>
          <div className="relative">
            <input
              id="sf-confirmPassword"
              required
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleOnChange}
              placeholder="Repeat password"
              className={`${DS.input.base} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-richblack-400 transition-colors hover:text-richblack-200"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              {showConfirmPassword ? (
                <AiOutlineEyeInvisible size={18} />
              ) : (
                <AiOutlineEye size={18} />
              )}
            </button>
          </div>
        </div>
      </div>

      <button type="submit" className={`${DS.btn.primary} mt-1 w-full`}>
        Create Account
      </button>

      <p className="text-center text-sm text-richblack-400">
        Already have an account?{" "}
        <Link
          to="/login"
          className="font-medium text-yellow-50 transition-colors hover:text-yellow-25"
        >
          Sign in
        </Link>
      </p>
    </form>
  );
}

export default SignupForm;
